export type ViewportDirection = "earlier" | "later"

export interface ViewportRange {
  startsAt: number
  endsAt: number
}

export interface ViewportLoadResult<T> extends ViewportRange {
  points: T[]
}

interface ViewportOptions<T> {
  points: T[]
  position: (point: T) => number
  viewport: ViewportRange
  coverage: ViewportRange
  loaded?: ViewportRange
  minimumSpan?: number
  loadRange?: (range: ViewportRange, direction: ViewportDirection) => Promise<ViewportLoadResult<T>>
  onChange: () => void
  onLoadingChange?: (direction?: ViewportDirection, error?: Error) => void
}

export class TimeSeriesViewport<T> {
  private pointsByPosition = new Map<number, T>()
  private sortedPoints?: T[]
  private viewportRange: ViewportRange
  private readonly initialViewportRange: ViewportRange
  private readonly coverageRange: ViewportRange
  private loadedRange: ViewportRange
  private loadingDirection?: ViewportDirection

  constructor(private readonly options: ViewportOptions<T>) {
    options.points.forEach((point) => this.pointsByPosition.set(options.position(point), point))
    this.coverageRange = this.ordered(options.coverage)
    this.viewportRange = this.clamp(this.ordered(options.viewport))
    this.initialViewportRange = { ...this.viewportRange }
    this.loadedRange = this.ordered(options.loaded || options.viewport)
  }

  get range() {
    return { ...this.viewportRange }
  }

  get coverage() {
    return { ...this.coverageRange }
  }

  get points() {
    this.sortedPoints ||= [...this.pointsByPosition.entries()]
      .sort(([left], [right]) => left - right)
      .map(([, point]) => point)
    return this.sortedPoints
  }

  get visiblePoints() {
    const { startsAt, endsAt } = this.viewportRange
    return this.points.filter((point) => {
      const position = this.options.position(point)
      return position >= startsAt && position <= endsAt
    })
  }

  get loading() {
    return this.loadingDirection
  }

  get canReset() {
    return this.viewportRange.startsAt !== this.initialViewportRange.startsAt ||
      this.viewportRange.endsAt !== this.initialViewportRange.endsAt
  }

  canPan(direction: ViewportDirection) {
    const epsilon = Math.max((this.viewportRange.endsAt - this.viewportRange.startsAt) * 0.001, 1)
    return direction === "earlier"
      ? this.viewportRange.startsAt > this.coverageRange.startsAt + epsilon
      : this.viewportRange.endsAt < this.coverageRange.endsAt - epsilon
  }

  panByFraction(fraction: number) {
    if (!Number.isFinite(fraction) || fraction === 0) return

    const span = this.viewportRange.endsAt - this.viewportRange.startsAt
    const delta = span * Math.max(-0.8, Math.min(fraction, 0.8))
    const direction: ViewportDirection = delta < 0 ? "earlier" : "later"
    const previous = this.viewportRange
    this.viewportRange = this.clamp({ startsAt: previous.startsAt + delta, endsAt: previous.endsAt + delta })
    if (this.viewportRange.startsAt === previous.startsAt && this.viewportRange.endsAt === previous.endsAt) return

    this.options.onChange()
    void this.loadIfNeeded(direction)
  }

  page(direction: ViewportDirection) {
    this.panByFraction(direction === "earlier" ? -0.8 : 0.8)
  }

  zoomByFactor(factor: number, anchorFraction = 0.5) {
    if (!Number.isFinite(factor) || factor <= 0 || factor === 1) return

    const previous = this.viewportRange
    const span = previous.endsAt - previous.startsAt
    const coverageSpan = this.coverageRange.endsAt - this.coverageRange.startsAt
    const minimumSpan = Math.min(Math.max(this.options.minimumSpan || coverageSpan / 10_000, 1), coverageSpan)
    const nextSpan = Math.max(minimumSpan, Math.min(span * factor, coverageSpan))
    const anchor = Math.max(0, Math.min(anchorFraction, 1))
    const anchorAt = previous.startsAt + span * anchor
    this.setViewport({
      startsAt: anchorAt - nextSpan * anchor,
      endsAt: anchorAt + nextSpan * (1 - anchor)
    }, previous)
  }

  zoomTo(range: ViewportRange) {
    const ordered = this.ordered(range)
    const currentSpan = this.viewportRange.endsAt - this.viewportRange.startsAt
    const selectedSpan = ordered.endsAt - ordered.startsAt
    if (selectedSpan < Math.max(currentSpan * 0.015, this.options.minimumSpan || 1)) return

    this.setViewport(ordered, this.viewportRange)
  }

  reset() {
    this.setViewport(this.initialViewportRange, this.viewportRange)
  }

  private setViewport(range: ViewportRange, previous: ViewportRange) {
    this.viewportRange = this.clamp(range)
    if (this.viewportRange.startsAt === previous.startsAt && this.viewportRange.endsAt === previous.endsAt) return

    this.options.onChange()
    const earlierGrowth = Math.max(previous.startsAt - this.viewportRange.startsAt, 0)
    const laterGrowth = Math.max(this.viewportRange.endsAt - previous.endsAt, 0)
    const preferred = earlierGrowth >= laterGrowth ? "earlier" : "later"
    void this.loadIfNeeded(preferred)
  }

  private async loadIfNeeded(direction: ViewportDirection) {
    if (!this.options.loadRange || this.loadingDirection) return

    const span = this.viewportRange.endsAt - this.viewportRange.startsAt
    const threshold = span * 0.18
    const needsEarlier = direction === "earlier" &&
      this.viewportRange.startsAt <= this.loadedRange.startsAt + threshold &&
      this.loadedRange.startsAt > this.coverageRange.startsAt
    const needsLater = direction === "later" &&
      this.viewportRange.endsAt >= this.loadedRange.endsAt - threshold &&
      this.loadedRange.endsAt < this.coverageRange.endsAt
    if (!needsEarlier && !needsLater) return

    const gap = 1
    const requested = direction === "earlier"
      ? {
          startsAt: Math.max(this.coverageRange.startsAt, this.loadedRange.startsAt - span),
          endsAt: this.loadedRange.startsAt - gap
        }
      : {
          startsAt: this.loadedRange.endsAt + gap,
          endsAt: Math.min(this.coverageRange.endsAt, this.loadedRange.endsAt + span)
        }

    this.loadingDirection = direction
    this.options.onLoadingChange?.(direction)
    try {
      const result = await this.options.loadRange(requested, direction)
      result.points.forEach((point) => this.pointsByPosition.set(this.options.position(point), point))
      this.sortedPoints = undefined
      this.loadedRange = {
        startsAt: Math.min(this.loadedRange.startsAt, result.startsAt),
        endsAt: Math.max(this.loadedRange.endsAt, result.endsAt)
      }
      this.loadingDirection = undefined
      this.options.onLoadingChange?.()
      this.options.onChange()
      void this.loadIfNeeded(direction === "earlier" ? "later" : "earlier")
    } catch (caught) {
      this.loadingDirection = undefined
      const error = caught instanceof Error ? caught : new Error("Chart data could not be loaded")
      this.options.onLoadingChange?.(undefined, error)
    }
  }

  private clamp(range: ViewportRange) {
    const coverageSpan = this.coverageRange.endsAt - this.coverageRange.startsAt
    const span = Math.min(range.endsAt - range.startsAt, coverageSpan)
    let startsAt = range.startsAt
    let endsAt = startsAt + span

    if (startsAt < this.coverageRange.startsAt) {
      startsAt = this.coverageRange.startsAt
      endsAt = startsAt + span
    }
    if (endsAt > this.coverageRange.endsAt) {
      endsAt = this.coverageRange.endsAt
      startsAt = endsAt - span
    }
    return { startsAt, endsAt }
  }

  private ordered(range: ViewportRange) {
    return range.startsAt <= range.endsAt
      ? { ...range }
      : { startsAt: range.endsAt, endsAt: range.startsAt }
  }
}

interface GestureOptions {
  onPan: (fraction: number) => void
  onZoom?: (factor: number, anchorFraction: number) => void
  onSelect?: (startsFraction: number, endsFraction: number) => void
  onHover?: (event: PointerEvent) => void
  onLeave?: () => void
}

export class TimeSeriesGestures {
  private pointerId?: number
  private previousX = 0
  private originX = 0
  private dragging = false
  private selecting = false
  private selection?: HTMLDivElement

  constructor(private readonly element: HTMLElement, private readonly options: GestureOptions) {}

  connect() {
    this.element.addEventListener("pointerdown", this.pointerDown)
    this.element.addEventListener("pointermove", this.pointerMove)
    this.element.addEventListener("pointerup", this.pointerUp)
    this.element.addEventListener("pointercancel", this.pointerUp)
    this.element.addEventListener("pointerleave", this.pointerLeave)
    this.element.addEventListener("wheel", this.wheel, { passive: false })
    if (this.options.onSelect) {
      this.selection = document.createElement("div")
      this.selection.className = "time-series-selection"
      this.selection.setAttribute("aria-hidden", "true")
      this.selection.hidden = true
      this.element.parentElement?.append(this.selection)
      this.element.classList.add("can-select-range")
    }
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.pointerDown)
    this.element.removeEventListener("pointermove", this.pointerMove)
    this.element.removeEventListener("pointerup", this.pointerUp)
    this.element.removeEventListener("pointercancel", this.pointerUp)
    this.element.removeEventListener("pointerleave", this.pointerLeave)
    this.element.removeEventListener("wheel", this.wheel)
    this.selection?.remove()
    this.element.classList.remove("can-select-range")
  }

  private pointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return
    this.pointerId = event.pointerId
    this.originX = event.clientX
    this.previousX = event.clientX
    this.dragging = false
    this.selecting = event.pointerType === "mouse" && Boolean(this.options.onSelect)
    this.element.setPointerCapture(event.pointerId)
  }

  private pointerMove = (event: PointerEvent) => {
    if (this.pointerId !== event.pointerId) {
      this.options.onHover?.(event)
      return
    }

    if (!this.dragging && Math.abs(event.clientX - this.originX) >= 5) {
      this.dragging = true
      this.element.classList.add("is-dragging")
      this.options.onLeave?.()
    }
    if (!this.dragging) return

    event.preventDefault()
    if (this.selecting) {
      this.updateSelection(event.clientX)
      return
    }
    const width = Math.max(this.element.getBoundingClientRect().width, 1)
    this.options.onPan(-(event.clientX - this.previousX) / width)
    this.previousX = event.clientX
  }

  private pointerUp = (event: PointerEvent) => {
    if (this.pointerId !== event.pointerId) return
    if (this.dragging && this.selecting) this.finishSelection(event.clientX)
    if (this.element.hasPointerCapture(event.pointerId)) this.element.releasePointerCapture(event.pointerId)
    this.pointerId = undefined
    this.dragging = false
    this.selecting = false
    this.element.classList.remove("is-dragging")
  }

  private pointerLeave = () => {
    if (!this.dragging) this.options.onLeave?.()
  }

  private wheel = (event: WheelEvent) => {
    const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY)
    if (!horizontal && !event.shiftKey && this.options.onZoom) {
      event.preventDefault()
      const bounds = this.element.getBoundingClientRect()
      const anchor = Math.max(0, Math.min((event.clientX - bounds.left) / Math.max(bounds.width, 1), 1))
      const factor = Math.max(0.72, Math.min(Math.exp(event.deltaY * 0.0015), 1.38))
      this.options.onZoom(factor, anchor)
      return
    }
    if (!horizontal && !event.shiftKey) return

    event.preventDefault()
    const delta = horizontal ? event.deltaX : event.deltaY
    const width = Math.max(this.element.getBoundingClientRect().width, 1)
    this.options.onPan(Math.max(-0.35, Math.min(delta / width, 0.35)))
  }

  private updateSelection(clientX: number) {
    if (!this.selection) return
    const bounds = this.element.getBoundingClientRect()
    const origin = Math.max(0, Math.min(this.originX - bounds.left, bounds.width))
    const current = Math.max(0, Math.min(clientX - bounds.left, bounds.width))
    this.selection.hidden = false
    this.selection.style.left = `${this.element.offsetLeft + Math.min(origin, current)}px`
    this.selection.style.width = `${Math.abs(current - origin)}px`
  }

  private finishSelection(clientX: number) {
    if (!this.selection) return
    const bounds = this.element.getBoundingClientRect()
    const startsFraction = Math.max(0, Math.min((Math.min(this.originX, clientX) - bounds.left) / Math.max(bounds.width, 1), 1))
    const endsFraction = Math.max(0, Math.min((Math.max(this.originX, clientX) - bounds.left) / Math.max(bounds.width, 1), 1))
    this.selection.hidden = true
    this.selection.style.width = "0"
    if (Math.abs(clientX - this.originX) >= 8) this.options.onSelect?.(startsFraction, endsFraction)
  }
}
